import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Printer, ArrowLeft, Phone, Mail, Globe, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { sendViaEmail, uploadDocument, buildWhatsAppUrl } from "@/lib/pdfSend";
import { docTexts } from "@/lib/docTexts";

const COMPANY = {
  name: "BLU LODGE PVT LTD",
  address: "G. Moonimaage, 8th floor North Wing · Dharumavantha Magu · 20128 K. Malé · Maldives",
  phone: "+960 9356335",
  email: "info@blulodgemaldives.com",
  website: "https://www.blulodgemaldives.com",
};
const titleFor = (g) => {
  const s = (g.sex || "").toUpperCase();
  if (s.startsWith("F")) return "MRS";
  if (s.startsWith("M")) return "MR";
  return "";
};

export default function VoucherGroup() {
  const { gid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/bookings/group/${gid}/full`).then((r) => setData(r.data)).catch(() => setError("Gruppo non trovato"));
  }, [gid]);

  if (error) return <div className="p-10 text-center text-slate-500">{error}</div>;
  if (!data) return <div className="p-10 text-center text-slate-400">Caricamento…</div>;

  const rooms = data.rooms || [];
  const t = docTexts(data.settings);
  const holder = rooms[0]?.booking || {};
  const ownerId = holder.id;
  const ref = (gid || "").slice(0, 8).toUpperCase();

  const doSend = async (channel) => {
    if (sending) return;
    const el = document.querySelector('[data-testid="voucher-sheet"]');
    if (!el) return;
    setSending(true);
    try {
      if (channel === "email") {
        const email = holder.guest_email || window.prompt("Email destinatario:") || "";
        if (!email) { setSending(false); return; }
        await sendViaEmail(ownerId, "voucher", el, email);
        toast.success(`Voucher inviato a ${email}`);
      } else {
        const phone = holder.guest_phone || window.prompt("Numero WhatsApp (con prefisso, es. +39...):") || "";
        if (!phone) { setSending(false); return; }
        const d = await uploadDocument(ownerId, "voucher", el);
        toast.success("Apertura WhatsApp…");
        window.location.href = buildWhatsAppUrl(d.public_url, "voucher", phone, `${holder.guest_name} ${holder.guest_surname}`);
      }
    } catch (e) { toast.error("Invio non riuscito"); }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } .sheet { box-shadow: none !important; margin: 0 !important; } }`}</style>
      <div className="max-w-3xl mx-auto mb-4 flex justify-between no-print">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"><ArrowLeft size={16} /> Indietro</button>
        <div className="flex gap-2">
          <button onClick={() => doSend("email")} disabled={sending} data-testid="send-voucher-email" className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-50"><Mail size={16} /> Email</button>
          <button onClick={() => doSend("whatsapp")} disabled={sending} data-testid="send-voucher-whatsapp" className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-50"><MessageCircle size={16} /> WhatsApp</button>
          <button onClick={() => window.print()} data-testid="print-voucher" className="flex items-center gap-2 bg-sky-700 hover:bg-sky-800 text-white text-sm font-semibold px-4 py-2 rounded-md"><Printer size={16} /> Stampa</button>
        </div>
      </div>

      <div className="sheet max-w-3xl mx-auto bg-white shadow-sm overflow-hidden" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="voucher-sheet">
        <div className="bg-slate-900 text-white px-8 py-6 flex items-center gap-5">
          <img src="/blu-lodge-logo.png" alt="Blu Lodge Maldives" className="w-20 h-20 object-contain bg-white rounded-lg p-2 shrink-0" data-testid="voucher-logo" />
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>{COMPANY.name}</h1>
            <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[11px] text-slate-300">
              <div className="flex items-center gap-1.5"><MapPin size={12} /> {COMPANY.address}</div>
              <div className="flex items-center gap-1.5"><Phone size={12} /> {COMPANY.phone}</div>
              <div className="flex items-center gap-1.5"><Mail size={12} /> {COMPANY.email}</div>
              <div className="flex items-center gap-1.5"><Globe size={12} /> {COMPANY.website.replace("https://", "")}</div>
            </div>
          </div>
        </div>

        <div className="px-8 py-6">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-slate-800 pb-2">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Voucher di gruppo · {rooms.length} camere</div>
            <div className="font-mono font-bold text-slate-900">#{ref}</div>
          </div>
          <div className="py-4">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Capogruppo</div>
            <div className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>{`${holder.guest_name || ""} ${holder.guest_surname || ""}`.trim() || "—"}</div>
            <div className="text-xs text-slate-500 mt-1">Soggiorno: {holder.checkin} → {holder.checkout}</div>
          </div>

          {rooms.map((rm, idx) => {
            const b = rm.booking; const guests = b.guests || [];
            return (
              <div key={b.id} className="mb-5 rounded-md border border-slate-200 overflow-hidden">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 font-bold text-slate-800 text-sm flex justify-between" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  <span>Camera {idx + 1}: {rm.room.name || "—"}{rm.room.type ? ` · ${rm.room.type}` : ""}</span>
                  <span className="font-normal text-slate-500">{rm.structure.name || ""}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-4 text-sm">
                  <Info label="Ospiti" value={`${b.adults || 0} adulti · ${b.children || 0} bambini`} />
                  <Info label="Check-in" value={`${b.checkin} ${b.checkin_time || ""}`} />
                  <Info label="Check-out" value={`${b.checkout} ${b.checkout_time || ""}`} />
                  <Info label="Notti" value={rm.nights} />
                  <Info label="Volo" value={b.flight || "—"} />
                </div>
                {guests.length > 0 && (
                  <table className="w-full text-sm border-t border-slate-200">
                    <thead>
                      <tr className="text-left text-xs uppercase text-slate-500 bg-slate-50">
                        <th className="px-4 py-2">Ospite</th><th className="px-4 py-2">Passaporto</th><th className="px-4 py-2">Nascita</th><th className="px-4 py-2">Nazionalità</th><th className="px-4 py-2">Scadenza</th>
                      </tr>
                    </thead>
                    <tbody>
                      {guests.map((g, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-4 py-2 font-medium text-slate-800">{[titleFor(g), (g.name || "").toUpperCase(), (g.surname || "").toUpperCase()].filter(Boolean).join(" ") || "—"}</td>
                          <td className="px-4 py-2 text-slate-600 font-mono text-xs">{g.passport_number || "—"}</td>
                          <td className="px-4 py-2 text-slate-600">{g.birth_date || "—"}</td>
                          <td className="px-4 py-2 text-slate-600">{g.nationality || "—"}</td>
                          <td className="px-4 py-2 text-slate-600">{g.expiry_date || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}

          <div className="mt-2 flex flex-wrap gap-x-8 gap-y-1 text-xs text-slate-600">
            <span><b className="text-slate-800">Check-in:</b> dalle 14:00 alle 18:00</span>
            <span><b className="text-slate-800">Check-out:</b> fino alle 10:00</span>
            <span><b className="text-slate-800">Età minima:</b> 5 anni</span>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500 border-t border-slate-200 pt-4">
            <span className="flex items-center gap-1.5"><Phone size={12} /> {COMPANY.phone}</span>
            <span className="flex items-center gap-1.5"><Mail size={12} /> {COMPANY.email}</span>
            <a href={COMPANY.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sky-700 underline"><Globe size={12} /> {COMPANY.website.replace("https://", "")}</a>
          </div>
          <p className="mt-3 text-[11px] font-semibold text-slate-600 leading-relaxed">{t.nb}</p>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (<div><div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div><div className="font-semibold text-slate-800">{value}</div></div>);
}
